const RANK_CONSTANTS = {
    INITIAL_RANK: 1000000000, // 1B
    SPACING: 1000000, // 1M
    BATCH_SIZE: 25,
    MIN_RANK: 1, // Minimum possible rank value
    MAX_RANK: 2000000000 // 2B - Maximum possible rank value
};

class SprintRankService {
    constructor(azureService) {
        this.azureService = azureService;
    }

    async ensureIterationInitialized(items, iteration) {
        if (!Array.isArray(items)) {
            throw new Error('Items must be an array');
        }

        // Filter items for the specific iteration
        const iterationItems = items.filter(
            item => item.fields['System.IterationPath'] === iteration
        );

        try {
            // Sort all items by their current rank or natural order if no rank
            const sortedItems = this._sortByCurrentOrder(iterationItems);

            // Generate new ranks with consistent spacing for all items
            const newRanks = sortedItems.map((item, index) => ({
                id: item.id,
                rank: Math.min(
                    RANK_CONSTANTS.MAX_RANK,
                    RANK_CONSTANTS.INITIAL_RANK + (index * RANK_CONSTANTS.SPACING)
                )
            }));

            // Process updates in batches
            for (let i = 0; i < newRanks.length; i += RANK_CONSTANTS.BATCH_SIZE) {
                const chunk = newRanks.slice(i, i + RANK_CONSTANTS.BATCH_SIZE);
                await Promise.all(chunk.map(update =>
                    this.azureService.updateWorkItem(update.id, [{
                        field: 'Custom.SprintRank',
                        value: update.rank
                    }])
                ));
            }

            return true; // Signal that initialization occurred
        } catch (error) {
            console.error('Failed to initialize sprint ranks:', error);
            throw error;
        }
    }

    _sortByCurrentOrder(items) {
        return [...items].sort((a, b) => {
            // First try to sort by existing SprintRank
            const aRank = a.fields['Custom.SprintRank'];
            const bRank = b.fields['Custom.SprintRank'];

            if (aRank != null && bRank != null) {
                return aRank - bRank;
            }

            // If either item doesn't have a rank, fall back to natural order
            return this._compareByNaturalOrder(a, b);
        });
    }

    _compareByNaturalOrder(a, b) {
        // Try Stack Rank first
        const aStackRank = a.fields['Microsoft.VSTS.Common.StackRank'];
        const bStackRank = b.fields['Microsoft.VSTS.Common.StackRank'];

        if (aStackRank != null && bStackRank != null) {
            return aStackRank - bStackRank;
        }

        // Fall back to created date
        const aDate = new Date(a.fields['System.CreatedDate']).getTime();
        const bDate = new Date(b.fields['System.CreatedDate']).getTime();

        if (isNaN(aDate) || isNaN(bDate)) {
            return 0; // If dates are invalid, maintain original order
        }

        return aDate - bDate;
    }

    async handleReorder(draggedId, targetId, items) {
        if (!Array.isArray(items)) {
            throw new Error('Items must be an array');
        }

        const draggedItem = items.find(item => item.id === draggedId);
        const targetItem = items.find(item => item.id === targetId);

        if (!draggedItem || !targetItem) {
            throw new Error('Could not find dragged or target item');
        }

        // Verify items are in the same iteration
        if (draggedItem.fields['System.IterationPath'] !== targetItem.fields['System.IterationPath']) {
            throw new Error('Cannot reorder items across iterations');
        }

        // Get all items in the same iteration
        const iteration = draggedItem.fields['System.IterationPath'];
        const iterationItems = items
            .filter(item => item.fields['System.IterationPath'] === iteration)
            .sort((a, b) => (a.fields['Custom.SprintRank'] || Infinity) - (b.fields['Custom.SprintRank'] || Infinity));

        // Find the target position
        const targetIndex = iterationItems.findIndex(item => item.id === targetId);
        if (targetIndex === -1) {
            throw new Error('Target position not found');
        }

        // Calculate the new rank
        const newRank = this._calculateNewRank(iterationItems, targetIndex);

        try {
            // Update the work item with the new rank
            await this.azureService.updateWorkItem(draggedId, [{
                field: 'Custom.SprintRank',
                value: newRank
            }]);

            return newRank;
        } catch (error) {
            console.error('Failed to update work item rank:', error);
            throw error;
        }
    }

    _calculateNewRank(items, targetIndex) {
        // Get all valid ranks and their indices
        const itemsWithRanks = items
            .map((item, index) => ({
                index,
                rank: item.fields['Custom.SprintRank']
            }))
            .filter(item => item.rank != null)
            .sort((a, b) => a.rank - b.rank);

        // Handle empty list case
        if (itemsWithRanks.length === 0) {
            return RANK_CONSTANTS.INITIAL_RANK;
        }

        // Moving to the top
        if (targetIndex === 0) {
            const firstRank = itemsWithRanks[0].rank;
            return Math.max(
                RANK_CONSTANTS.MIN_RANK,
                Math.floor(firstRank / 2)
            );
        }

        // Moving to the bottom
        if (targetIndex >= items.length - 1) {
            const lastRank = itemsWithRanks[itemsWithRanks.length - 1].rank;
            return Math.min(
                RANK_CONSTANTS.MAX_RANK,
                lastRank + RANK_CONSTANTS.SPACING
            );
        }

        // Find the ranks of items before and after the target position
        const targetItem = itemsWithRanks.find(item => item.index === targetIndex);
        const prevItem = itemsWithRanks.filter(item => item.index < targetIndex).pop();
        const nextItem = itemsWithRanks.find(item => item.index > targetIndex);

        let prevRank = prevItem ? prevItem.rank : 0;
        let nextRank = nextItem ? nextItem.rank : prevRank + RANK_CONSTANTS.SPACING * 2;

        // If target position already has a rank, use the surrounding ranks
        if (targetItem) {
            // For upward movement, use the rank before the target
            if (prevItem) {
                nextRank = targetItem.rank;
                prevRank = prevItem.rank;
            }
            // For downward movement, use the rank after the target
            else if (nextItem) {
                prevRank = targetItem.rank;
                nextRank = nextItem.rank;
            }
        }

        // Calculate the midpoint, ensuring we stay within bounds
        return Math.min(
            RANK_CONSTANTS.MAX_RANK,
            Math.max(
                RANK_CONSTANTS.MIN_RANK,
                Math.floor(prevRank + ((nextRank - prevRank) / 2))
            )
        );
    }
}

export default SprintRankService;